import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core'

import { PAYMENT_STATUS_LABELS } from '@modules/private/payment/data/constants/payment.constants'

export type AdjustOperation = 'VOID_DUPLICATE' | 'CLOSE_REMAINDER' | 'REVERT'
/** Los tres grupos de la bandeja (posiblesDuplicados desde Exigibilidad F18). */
export type TrayGroup = 'remanentes' | 'posiblesDuplicados' | 'anulados'
/** De dónde se abrió el modal. `listado` es la fila de Pagos, para anular un duplicado recién cargado. */
export type AttentionGroup = TrayGroup | 'listado'

/** Por debajo de esto un remanente es residuo de redondeo; por encima, es dinero. */
const REMAINDER_LIMIT = 10

//* A nivel de módulo: los getters que lo usan corren en cada ciclo de detección de cambios y
//* construir un Intl.NumberFormat cada vez es caro.
const MXN = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })

//* La bitácora guarda el nombre del campo tal como vive en el pago; Operaciones lo lee en español y los
//* importes con formato de dinero. Lo que no está aquí se enseña como llegó. `pendingToApply` se nombra
//* igual que en el impacto y la ficha de este modal, para que el historial no hable de otra cosa.
const AUDIT_FIELD_LABELS: Readonly<Record<string, string>> = { amount: 'Monto', trackingKey: 'Clave de rastreo', pendingToApply: 'Disponible para aplicar' }
const AUDIT_MONEY_FIELDS = new Set(['amount', 'pendingToApply'])

/**
 * Ajuste de UN pago.
 *
 * Tres pasos que nunca quedan vacíos: elegir qué hacer, revisar el impacto, confirmar. El paso 3
 * aparece solo cuando ya hay algo que confirmar — **no se puede aplicar sin haber previsualizado**,
 * y cualquier cambio en la captura tira la previsualización.
 *
 * La bandeja de pagos por atender vive en su pestaña, no aquí: este modal se abre sobre un pago ya
 * elegido y su única responsabilidad es ajustarlo.
 */
@Component({
  selector: 'app-payment-adjust-modal',
  templateUrl: './payment-adjust-modal.component.html',
  styleUrls: ['./payment-adjust-modal.component.scss'],
})
export class PaymentAdjustModalComponent implements OnChanges {
  @Input() visible = false
  /** La fila elegida en la bandeja. */
  @Input() payment: any = null
  /** De qué grupo viene, que es lo que decide qué operaciones tienen sentido. */
  @Input() group: AttentionGroup = 'remanentes'
  @Input() previewing = false
  @Input() saving = false

  //* Binding de DOS vías: con `[visible]` de una sola vía + `(onHide)`, el change detection
  //* re-empuja el valor viejo y el modal se reabre (bug real documentado en ui-styleguide.md §3.12).
  @Output() visibleChange = new EventEmitter<boolean>()
  @Output() onPreview = new EventEmitter<{ paymentId: string; payload: any }>()
  @Output() onApply = new EventEmitter<{ paymentId: string; payload: any }>()
  /** Pide al padre el estado, las aplicaciones y la bitácora del pago. */
  @Output() onLoadContext = new EventEmitter<string>()

  operation: AdjustOperation | null = null
  reason = ''
  remainderThreshold = REMAINDER_LIMIT

  preview: any = null
  previewError = ''
  confirmations: Record<string, boolean> = {}

  /** Estado + aplicaciones + bitácora del pago, lo entrega el padre. */
  context: any = null
  contextLoading = false
  auditOpen = false
  appsOpen = false
  /** Entrada de bitácora que se va a deshacer con REVERT. */
  auditEntryId: string | null = null

  confirmingClose = false

  readonly statusLabels = PAYMENT_STATUS_LABELS

  readonly warningLabels: Record<string, string> = {
    MATERIAL_REMAINDER: 'El remanente no es un residuo de redondeo',
    HAS_APPLICATIONS: 'El pago tiene aplicaciones vivas',
    NO_TWIN_APPLIED: 'No hay otro pago aplicado con esta clave y monto',
    STATE_MOVED_SINCE: 'El pago cambió después de ese ajuste',
    MAY_BE_ANOTHER_LEASE: 'Podría ser la renta de otro arrendamiento',
    CAN_STILL_BE_APPLIED: 'Este saldo todavía se puede aplicar',
  }

  //! `PAYMENT_RESTORE_PENDING` se queda aunque la operación ya no exista: hay bitácoras con esa
  //! entrada y sin su etiqueta el historial mostraría el código crudo justo donde el operador va a
  //! decidir si lo deshace. Las etiquetas describen lo que PASÓ, no lo que hoy se puede hacer.
  readonly actionLabels: Record<string, string> = {
    PAYMENT_RESTORE_PENDING: 'Se destrabó el pago',
    PAYMENT_VOID_DUPLICATE: 'Se anuló por carga repetida',
    PAYMENT_CLOSE_REMAINDER: 'Se cerró el remanente',
    PAYMENT_REVERT: 'Se deshizo un ajuste',
    //* F-045 · las escribe «Editar pago» en Dinero. No son deshacibles: no llevan `metadata.operation`.
    PAYMENT_AMOUNT_CORRECTED: 'Monto corregido',
    PAYMENT_TRACKING_KEY_CORRECTED: 'Clave de rastreo corregida',
    PAYMENT_DUPLICATE_CONFIRMED: 'Duplicado confirmado',
  }

  readonly operations: { key: AdjustOperation; icon: string; title: string; hint: string }[] = [
    { key: 'VOID_DUPLICATE', icon: 'fa-solid fa-ban', title: 'Anular carga repetida', hint: 'Saca el pago de circulación. Úsalo solo cuando el dinero ya entró por otra copia.' },
    { key: 'CLOSE_REMAINDER', icon: 'fa-solid fa-broom', title: 'Cerrar el remanente', hint: 'Condona el residuo que impide que el pago quede aplicado por completo.' },
    { key: 'REVERT', icon: 'fa-solid fa-rotate-left', title: 'Deshacer el ajuste', hint: 'Devuelve el pago exactamente al estado que la bitácora guardó antes del ajuste.' },
  ]

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) this.reset()
  }

  private reset(): void {
    this.operation = this.defaultOperation()
    this.auditEntryId = this.payment?.ultimoAjuste?._id ?? null
    this.reason = ''
    this.remainderThreshold = REMAINDER_LIMIT
    this.confirmingClose = false
    this.invalidatePreview()

    //* El contexto se pide siempre: son los saldos, a qué arrendamiento fue el dinero y qué se le
    //* hizo antes al pago. El historial se abre de entrada solo al deshacer, que es cuando hay que
    //* elegir sobre cuál entrada actuar.
    this.context = null
    this.auditOpen = this.group === 'anulados'
    this.appsOpen = false
    if (this.payment?._id) {
      this.contextLoading = true
      this.onLoadContext.emit(this.payment._id)
    }
  }

  /** El padre entrega el contexto. */
  setContext(context: any): void {
    this.contextLoading = false
    this.context = context
  }

  /**
   * En los posibles duplicados **no se preselecciona nada**, a propósito.
   *
   * Antes se marcaba «anular» cuando existía otro pago aplicado con la misma clave y monto. Medido
   * en producción, esa señal es falsa la mayoría de las veces —compartir clave y monto es la forma
   * normal de un cliente con varios arrendamientos pagando la misma renta en cada uno—, así que
   * llegar con la acción destructiva ya marcada invitaba a borrar pagos legítimos. Donde solo cabe
   * una operación sí se marca, porque ahí no hay decisión que tomar.
   */
  private defaultOperation(): AdjustOperation | null {
    if (this.group === 'anulados') return 'REVERT'
    if (this.group === 'remanentes') return 'CLOSE_REMAINDER'
    //* Desde el listado la intención ya es explícita: el operador dio clic en «anular duplicado».
    if (this.group === 'listado') return 'VOID_DUPLICATE'
    return null
  }

  // ── Paso 1 · qué hacer ───────────────────────────────────────────────────

  /**
   * Solo las operaciones que tienen sentido para este pago; el resto ni se ofrece.
   *
   * `listado` NO tiene rama propia, y eso importa: la tenía, con un `return` temprano que devolvía
   * solo «Anular». Como el historial se pinta en todos los grupos, pulsar «Deshacer este» sobre un
   * pago abierto desde Dinero dejaba `operation` en `REVERT` mientras la única ficha visible seguía
   * siendo «Anular», sin marcar — la pantalla contaba una cosa y el impacto de abajo, otra.
   */
  get availableOperations(): { key: AdjustOperation; icon: string; title: string; hint: string }[] {
    if (this.group === 'anulados') return this.operations.filter((o) => o.key === 'REVERT')

    const keys: AdjustOperation[] = this.group === 'remanentes' ? ['CLOSE_REMAINDER'] : ['VOID_DUPLICATE']
    //* Deshacer se ofrece también aquí cuando el usuario lo eligió desde el historial.
    if (this.operation === 'REVERT') keys.push('REVERT')

    return this.operations.filter((o) => keys.includes(o.key))
  }

  selectOperation(op: AdjustOperation): void {
    this.operation = op
    //* El motivo se limpia: uno escrito para "cerrar el remanente" no justifica un "anular", y
    //* quedaría en la bitácora describiendo algo que no fue.
    this.reason = ''
    if (op !== 'REVERT') this.auditEntryId = null
    this.invalidatePreview()
  }

  /**
   * Qué pasa —y sobre todo qué NO pasa— si se aplica la acción elegida.
   *
   * Operaciones abrió el modal preguntando si se cancela la aplicación, a dónde van los centavos y
   * si esto afecta al arrendamiento. La respuesta a casi todo es «no se toca», pero eso no estaba
   * dicho en ninguna parte: el modal solo hablaba de los campos del pago. Aquí se dice explícito,
   * incluido el paso que falta después.
   *
   * `tono`: 'toca' lo que cambia · 'intacto' lo que no · 'ojo' lo que hay que entender bien ·
   * 'despues' el siguiente paso.
   */
  get efectos(): { tono: 'toca' | 'intacto' | 'ojo' | 'despues'; que: string; texto: string }[] {
    const sobra = MXN.format(this.payment?.remanente ?? 0)

    switch (this.operation) {
      case 'VOID_DUPLICATE':
        return [
          { tono: 'toca', que: 'El pago', texto: 'Sale de circulación: deja de aparecer en el listado de Pagos y en las búsquedas.' },
          { tono: 'ojo', que: 'Sus aplicaciones', texto: 'NO se cancelan. Si este pago tiene aplicaciones siguen vivas — por eso la herramienta bloquea cuando las hay.' },
          { tono: 'intacto', que: 'El arrendamiento', texto: 'No cambia nada. Si el pago tenía aplicaciones, el arrendamiento las va a seguir viendo.' },
          { tono: 'despues', que: 'Después', texto: 'Nada. Si te equivocaste, queda en la pestaña Anulados con su botón de deshacer.' },
        ]

      case 'CLOSE_REMAINDER':
        return [
          { tono: 'toca', que: 'El pago', texto: `Los ${sobra} que sobran dejan de estar disponibles y el pago queda como APLICADO.` },
          { tono: 'ojo', que: 'Ese saldo', texto: 'Se condona. Ninguna letra lo recibe: el cliente pierde ese crédito y simplemente deja de ser saldo aplicable.' },
          { tono: 'intacto', que: 'El arrendamiento', texto: 'No cambia nada. Sus letras quedan como están.' },
          {
            tono: 'despues',
            que: 'Antes de esto',
            texto: this.payment?.sePuedeAplicar
              ? 'Su arrendamiento TODAVÍA debe letras: lo que corresponde es aplicar ese saldo por el flujo normal, no condonarlo.'
              : 'Nada más: sus letras ya están cubiertas, así que ese residuo no tiene a dónde aplicarse.',
          },
        ]

      case 'REVERT':
        return [
          { tono: 'toca', que: 'El pago', texto: 'Vuelve exactamente al estado que la bitácora guardó antes del ajuste.' },
          { tono: 'intacto', que: 'Sus aplicaciones', texto: 'No se tocan.' },
          { tono: 'intacto', que: 'El arrendamiento', texto: 'No cambia nada.' },
          { tono: 'despues', que: 'Después', texto: 'El pago regresa a la bandeja que le corresponda.' },
        ]

      default:
        return []
    }
  }

  // ── Paso 2 · impacto ─────────────────────────────────────────────────────

  /** Cualquier cambio en la captura tira la previsualización: el usuario debe recalcular. */
  invalidatePreview(): void {
    this.preview = null
    this.previewError = ''
    this.confirmations = {}
  }

  get canPreview(): boolean {
    if (!this.payment || !this.operation) return false
    if (this.operation === 'CLOSE_REMAINDER') return this.remainderThreshold > 0
    if (this.operation === 'REVERT') return !!this.auditEntryId
    return true
  }

  requestPreview(): void {
    this.previewError = ''
    this.onPreview.emit({ paymentId: this.payment._id, payload: this.buildPayload() })
  }

  /** El padre entrega el resultado del dryRun. */
  setPreview(result: any, error?: string): void {
    if (error) {
      this.preview = null
      this.previewError = error
      return
    }
    this.preview = result
    this.confirmations = {}
  }

  /**
   * Lo que cambia el ajuste, en las tres cifras que le importan al operador. Se pintan las tres
   * siempre —no solo las que se mueven— para que también quede claro qué NO cambia.
   */
  get impactRows(): { label: string; antes: string; despues: string; cambia: boolean }[] {
    if (!this.preview) return []
    const b = this.preview.before || {}
    const a = this.preview.after || {}
    return [
      { label: 'Estado del pago', antes: this.statusLabel(b.status), despues: this.statusLabel(a.status), cambia: b.status !== a.status },
      { label: 'Disponible para aplicar', antes: MXN.format(b.pendingToApply || 0), despues: MXN.format(a.pendingToApply || 0), cambia: b.pendingToApply !== a.pendingToApply },
      { label: 'Vigencia', antes: b.deleted ? 'Anulado' : 'Vigente', despues: a.deleted ? 'Anulado' : 'Vigente', cambia: !!b.deleted !== !!a.deleted },
    ]
  }

  get blockingWarnings(): any[] {
    return (this.preview?.warnings || []).filter((w: any) => w.blocking)
  }

  get allWarningsConfirmed(): boolean {
    return this.blockingWarnings.every((w: any) => this.confirmations[w.code])
  }

  // ── Paso 3 · confirmar ───────────────────────────────────────────────────

  get canApply(): boolean {
    return !!this.preview && !!this.reason.trim() && this.allWarningsConfirmed && !this.saving
  }

  buildPayload(): any {
    const payload: any = { operation: this.operation, reason: this.reason.trim() }
    if (this.preview) {
      payload.stateHash = this.preview.stateHash
      payload.confirmations = Object.keys(this.confirmations).filter((k) => this.confirmations[k])
    }
    if (this.operation === 'CLOSE_REMAINDER') payload.remainderThreshold = Number(this.remainderThreshold)
    if (this.operation === 'REVERT') payload.auditEntryId = this.auditEntryId
    return payload
  }

  apply(): void {
    if (!this.canApply) return
    this.onApply.emit({ paymentId: this.payment._id, payload: this.buildPayload() })
  }

  // ── Estado y aplicaciones ────────────────────────────────────────────────

  /**
   * Los saldos del pago, siempre a la vista. Salen del contexto cuando ya llegó, y de la fila
   * mientras tanto: el modal no debe abrir sin un solo número, que era la queja de Operaciones.
   */
  get saldos(): { label: string; valor: string; hint?: string }[] {
    const p = this.context?.pago
    const monto = p?.monto ?? this.payment?.monto ?? 0
    const aplicado = p?.aplicado ?? this.payment?.aplicado ?? 0
    const disponible = p?.disponible ?? this.payment?.remanente ?? 0

    return [
      { label: 'Depósito', valor: MXN.format(monto), hint: 'Lo que el cliente pagó' },
      { label: 'Aplicado a letras', valor: MXN.format(aplicado), hint: 'Ya asignado a un arrendamiento' },
      { label: 'Disponible para aplicar', valor: MXN.format(disponible), hint: 'Lo que el selector puede usar hoy' },
      { label: 'Estado del pago', valor: this.statusLabel(p?.status ?? this.payment?.status), hint: (p?.anulado ?? this.payment?.anuladoEl) ? 'Anulado' : 'Vigente' },
    ]
  }

  get aplicaciones(): any[] {
    return this.context?.aplicaciones || []
  }

  /**
   * Los otros pagos con la misma clave y monto, y a dónde fue cada uno.
   *
   * Es lo que reemplaza al viejo veredicto de «copia repetida»: en vez de afirmar que el pago sobra
   * —cosa que el sistema no puede saber— se muestra la evidencia y decide quien tiene el estado de
   * cuenta enfrente.
   */
  get iguales(): any {
    return this.context?.iguales ?? null
  }

  get hayIguales(): boolean {
    return (this.iguales?.total ?? 1) > 1
  }

  /** Cuántos pagos iguales sobran respecto a los arrendamientos activos del cliente. */
  get sobran(): number {
    if (!this.iguales) return 0
    return this.iguales.total - (this.iguales.arrendamientosActivos ?? 0)
  }

  // ── Historial ────────────────────────────────────────────────────────────

  get auditEntries(): any[] {
    return this.context?.bitacora || []
  }

  /** Deshacer una entrada concreta del historial. */
  undoEntry(entry: any): void {
    this.operation = 'REVERT'
    this.auditEntryId = entry._id
    this.reason = ''
    this.invalidatePreview()
  }

  // ── Etiquetas ────────────────────────────────────────────────────────────

  warningLabel(code: string): string {
    return this.warningLabels[code] || code
  }

  statusLabel(status: string): string {
    return this.statusLabels[status] || status || '—'
  }

  actionLabel(action: string): string {
    return this.actionLabels[action] || action
  }

  auditFieldLabel(field: string): string {
    return AUDIT_FIELD_LABELS[field] || field
  }

  //* Solo se formatea un número: un campo de dinero sin valor formateado diría «$0.00», un importe que nunca
  //* existió, y un texto se enseña tal cual en vez de volverse «$NaN».
  auditValue(field: string, value: unknown): unknown {
    return AUDIT_MONEY_FIELDS.has(field) && typeof value === 'number' ? MXN.format(value) : value
  }

  //* «Duplicado confirmado» guarda solo el valor con que nació el pago: sin anterior, «→ B060-QA-2» se
  //* leería como si antes no hubiera tenido clave.
  hasAuditValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== ''
  }

  // ── Cierre ───────────────────────────────────────────────────────────────

  /**
   * Esc pasa por la misma puerta que la X y que Cancelar. El diálogo trae `closeOnEscape` apagado
   * para que PrimeNG no cierre de golpe saltándose la confirmación de descarte.
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible) this.requestHide()
  }

  get isDirty(): boolean {
    return !!this.preview || !!this.reason.trim()
  }

  requestHide(): void {
    if (this.isDirty && !this.confirmingClose) {
      this.confirmingClose = true
      return
    }
    this.hide()
  }

  cancelClose(): void {
    this.confirmingClose = false
  }

  hide(): void {
    this.confirmingClose = false
    this.visible = false
    this.visibleChange.emit(false)
  }
}
